import { useState, useEffect } from 'react';
import Button from '../../Common/Button/Button';
import { ErrorMessage } from '../../Common/ErrorMessage/ErrorMessage';
import { TABLE_TYPES, DEFAULT_TEMPLATE_ROW, TEMPLATE_COLUMNS, COLUMN_TYPES } from '../../Utils/constants';
import { parseTableColumns, filterNonActionColumns } from '../../Utils/tableHelpers';
import { getInputType } from '../../Utils/formatters';
import './RowForm.css';

/**
 * Row Form Component
 * Handles both template and custom table row editing
 */
const RowForm = ({
  table,
  initialData = null,
  onSubmit,
  onCancel,
  isLoading = false,
  error = null
}) => {
  const [formData, setFormData] = useState({});
  const [validationErrors, setValidationErrors] = useState({});

  // Initialize form data
  useEffect(() => {
    if (table?._type === TABLE_TYPES.CUSTOM) {
      const columns = parseTableColumns(table.column_value);
      const nonActionColumns = filterNonActionColumns(columns);
      
      const initialFormData = {};
      nonActionColumns.forEach(col => {
        if (col.type === COLUMN_TYPES.CELL_CHECKLIST) {
          // For cell-specific checklist columns, use the text value directly
          initialFormData[col.id] = initialData?.[col.id] || '';
        } else {
          initialFormData[col.id] = initialData?.[col.id] || '';
        }
      });
      setFormData(initialFormData);
    } else {
      setFormData(initialData || { ...DEFAULT_TEMPLATE_ROW });
    }
    
    setValidationErrors({});
  }, [table, initialData]);

  const handleInputChange = (fieldName) => (e) => {
    const value = e.target.value;
    setFormData(prev => ({
      ...prev,
      [fieldName]: value
    }));
    
    // Clear validation error when user starts typing
    if (validationErrors[fieldName]) {
      setValidationErrors(prev => ({
        ...prev,
        [fieldName]: null
      }));
    }
  };

  const validateForm = () => {
    const errors = {};
    
    if (table?._type === TABLE_TYPES.CUSTOM) {
      const columns = parseTableColumns(table.column_value);
      const nonActionColumns = filterNonActionColumns(columns);
      
      nonActionColumns.forEach(col => {
        if (col.required && !formData[col.id]?.trim()) {
          errors[col.id] = `${col.label} is required`;
        }
      });
    } else {
      // Template validation
      if (!formData.equipment?.trim()) {
        errors.equipment = 'Equipment is required';
      }
    }
    
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    onSubmit(formData);
  };

  const renderCustomTableForm = () => {
    const columns = parseTableColumns(table.column_value);
    const nonActionColumns = filterNonActionColumns(columns);
    
    return (
      <div className="dynamic-row-form">
        {nonActionColumns.map(col => (
          <div key={col.id} className="dynamic-form-group">
            <label className={`dynamic-form-label ${col.required ? 'form-field-required' : ''}`}>
              {col.label}
            </label>
            
            {col.type === 'textarea' ? (
              <textarea
                value={formData[col.id] || ''}
                onChange={handleInputChange(col.id)}
                className={`dynamic-form-input dynamic-form-textarea ${validationErrors[col.id] ? 'form-field-error' : ''}`}
                placeholder={`Enter ${col.label.toLowerCase()}`}
                disabled={isLoading}
              />
            ) : col.type === COLUMN_TYPES.CELL_CHECKLIST ? (
              <input
                type="text"
                value={formData[col.id] || ''}
                onChange={handleInputChange(col.id)}
                className={`dynamic-form-input ${validationErrors[col.id] ? 'form-field-error' : ''}`}
                placeholder={`Enter text or create checklist options (comma-separated)`}
                disabled={isLoading}
              />
            ) : (
              <input
                type={getInputType(col.type)}
                value={formData[col.id] || ''}
                onChange={handleInputChange(col.id)}
                className={`dynamic-form-input ${validationErrors[col.id] ? 'form-field-error' : ''}`}
                placeholder={`Enter ${col.label.toLowerCase()}`}
                disabled={isLoading}
              />
            )}
            
            {validationErrors[col.id] && (
              <div className="form-field-error-message">
                {validationErrors[col.id]}
              </div>
            )}
          </div>
        ))}
      </div>
    );
  };

  const renderTemplateForm = () => {
    return (
      <div className="row-form-content">
        {TEMPLATE_COLUMNS.map(column => (
          <div key={column.key} className="row-form-group">
            <label className={`row-form-label ${column.key === 'equipment' ? 'form-field-required' : ''}`}>
              {column.label}
            </label>
            
            {column.key === 'notes' ? (
              <textarea
                value={formData[column.key] || ''}
                onChange={handleInputChange(column.key)}
                className={`row-form-input row-form-textarea ${validationErrors[column.key] ? 'form-field-error' : ''}`}
                placeholder={`Enter ${column.label.toLowerCase()}`}
                disabled={isLoading}
                rows="3"
              />
            ) : (
              <input
                type="text"
                value={formData[column.key] || ''}
                onChange={handleInputChange(column.key)}
                className={`row-form-input ${validationErrors[column.key] ? 'form-field-error' : ''}`}
                placeholder={`Enter ${column.label.toLowerCase()}`}
                disabled={isLoading}
                required={column.key === 'equipment'}
              />
            )}
            
            {validationErrors[column.key] && (
              <div className="form-field-error-message">
                {validationErrors[column.key]}
              </div>
            )}
          </div>
        ))}
      </div>
    );
  };

  return (
    <form onSubmit={handleSubmit} className={`row-form ${isLoading ? 'row-form-loading' : ''}`}>
      <h3 className="row-form-title">
        {initialData ? 'Edit Row' : 'Add Row'}
      </h3>
      
      {error && <ErrorMessage message={error} />}
      
      {table?._type === TABLE_TYPES.CUSTOM 
        ? renderCustomTableForm() 
        : renderTemplateForm()
      }
      
      <div className="row-form-actions">
        <Button
          type="button"
          variant="secondary"
          onClick={onCancel}
          disabled={isLoading}
          className="row-form-cancel"
        >
          Cancel
        </Button>
        
        <Button
          type="submit"
          variant="primary"
          disabled={isLoading}
          className="row-form-submit"
        >
          {isLoading ? 'Saving...' : (initialData ? 'Update' : 'Add')}
        </Button>
      </div>
    </form>
  );
};

export default RowForm;
